/**
 * WW AI Models — viewport gate for the section's ambient motion
 * Author: Plaud Tayler — 2026-09-07
 * Version: 1.1.0 - 2026-09-17 18:30 (UTC+8) - Plaud Tayler — DTC-1296: an opt-in rotation that gives
 *   the stage to one model chip at a time
 * Version-note: 1.0.0 - 2026-09-07 15:35 (UTC+8) - Plaud Tayler — initial: owns the .is-live class that
 *   the chip drift and the tick level meter are declared under
 *
 * The section carries two perpetual animations: the model chips drift, and the tick rows read as
 * a level meter. Neither is declared unless the root has .is-live, and this file is the only
 * thing that sets it. Rule 6, question 1: an ambient loop must stop when nobody is looking, so
 * .is-live is tied to an IntersectionObserver and to page visibility. Questions 2 and 3 are
 * answered in the stylesheet (prefers-reduced-motion drops both animations; both are transform /
 * translate only, so there is no filter or blend-mode fallback to reason about).
 *
 * Progressive enhancement: with this file absent the section renders its comp still frame — marks
 * at their full height, chips on their comp coordinates. Nothing is hidden behind the motion.
 */
(function () {
  'use strict';
  if (window.__wwAiModels) return;
  window.__wwAiModels = true;

  var LIVE = 'is-live';
  var roots = [];

  /* DTC-1296: one chip at a time - Plaud Tayler - 2026-09-17 18:30 - Begin
     The rotation is a timer rather than a CSS animation because the number of chips is a block
     count, not something a keyframe percentage can know. The section already has a correct answer
     to Rule 6 question 1 in .is-live, so the timer simply rides it: out of view, or in a
     backgrounded tab, it is cleared rather than left running. Question 2 is this gate — under
     reduce the class is never added, so all three chips stay visible; the listener means a reader
     who turns the preference on mid-session gets them back without a reload. */
  var SOLO = 'is-solo';
  var SHOWN = 'is-shown';
  var motionOK = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: no-preference)')
    : { matches: true, addEventListener: null, addListener: null };

  function soloStop(root) {
    if (root.__wwAiTimer) {
      clearTimeout(root.__wwAiTimer);
      root.__wwAiTimer = null;
    }
  }

  /* hold on the current chip, fade it out, then hand the stage to the next one. The gap between
     the two is what the issue asks for: the outgoing chip is gone before the incoming one arrives,
     rather than the pair cross-fading through each other. */
  function soloTick(root) {
    root.__wwAiTimer = setTimeout(function () {
      var chips = root.__wwAiChips;
      chips[root.__wwAiIdx].classList.remove(SHOWN);
      root.__wwAiTimer = setTimeout(function () {
        root.__wwAiIdx = (root.__wwAiIdx + 1) % chips.length;
        chips[root.__wwAiIdx].classList.add(SHOWN);
        soloTick(root);
      }, root.__wwAiFade);
    }, root.__wwAiHold);
  }

  function soloStart(root) {
    if (!root.__wwAiSolo || root.__wwAiTimer) return;
    /* A stop can land in the gap, with nothing shown; come back to a chip on the stage. */
    root.__wwAiChips[root.__wwAiIdx].classList.add(SHOWN);
    soloTick(root);
  }

  function soloOn(root) {
    if (root.__wwAiSolo || !root.__wwAiChips || !motionOK.matches) return;
    root.__wwAiSolo = true;
    root.classList.add(SOLO);
    root.__wwAiChips[root.__wwAiIdx].classList.add(SHOWN);
  }

  function soloOff(root) {
    if (!root.__wwAiSolo) return;
    soloStop(root);
    root.__wwAiSolo = false;
    root.classList.remove(SOLO);
    for (var i = 0; i < root.__wwAiChips.length; i++) {
      root.__wwAiChips[i].classList.remove(SHOWN);
    }
  }

  function soloBind(root) {
    if (root.getAttribute('data-chip-solo') === null) return;
    var chips = root.querySelectorAll('.ww-ai__chip');
    /* Nothing to take turns with. */
    if (chips.length < 2) return;
    root.__wwAiChips = chips;
    root.__wwAiIdx = 0;
    root.__wwAiHold = parseInt(root.getAttribute('data-chip-hold'), 10) || 2200;
    root.__wwAiFade = parseInt(root.getAttribute('data-chip-fade'), 10) || 400;
    soloOn(root);
  }

  function onMotionChange() {
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (!root.__wwAiChips) continue;
      if (motionOK.matches) {
        soloOn(root);
        if (root.classList.contains(LIVE)) soloStart(root);
      } else {
        soloOff(root);
      }
    }
  }

  if (motionOK.addEventListener) motionOK.addEventListener('change', onMotionChange);
  else if (motionOK.addListener) motionOK.addListener(onMotionChange);
  /* DTC-1296: one chip at a time - Plaud Tayler - 2026-09-17 18:30 - End */

  function setLive(root, on) {
    if (on && !document.hidden && root.__wwAiInView) {
      root.classList.add(LIVE);
      soloStart(root);
    } else {
      root.classList.remove(LIVE);
      soloStop(root);
    }
  }

  function bind(root) {
    if (root.__wwAiBound) return;
    root.__wwAiBound = true;
    roots.push(root);
    soloBind(root);

    if (typeof IntersectionObserver !== 'function') {
      /* No observer: run it rather than never showing the motion at all. */
      root.__wwAiInView = true;
      setLive(root, true);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var el = entries[i].target;
        el.__wwAiInView = entries[i].isIntersecting;
        setLive(el, entries[i].isIntersecting);
      }
    }, { rootMargin: '10% 0px' });

    io.observe(root);
    root.__wwAiObserver = io;
  }

  /* A backgrounded tab keeps compositing paused animations cheap but not free, and a section left
     mid-cycle for hours is pure waste. */
  document.addEventListener('visibilitychange', function () {
    for (var i = 0; i < roots.length; i++) setLive(roots[i], !document.hidden);
  });

  function bindAll() {
    var els = document.querySelectorAll('.ww-ai');
    for (var i = 0; i < els.length; i++) bind(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }
  document.addEventListener('shopify:section:load', bindAll);
})();
